import { BP_VISITOR_ID_PARAM } from "../visitor/constants";
import { createVisitorId, normalizeVisitorId } from "../visitor/visitorId";
import { parseClickIdsFromSearch } from "../clickIds/parse";
import { isClickIdsFresh, mergeClickIds } from "../clickIds/merge";
import { parseUtmParamsFromSearch } from "../utm/parse";
import type { AnalyticsSession, AnalyticsStore } from "./types";

/**
 * Owns the visitor/click-id lifecycle (cache, write serialization, handoff
 * adoption) so host apps only provide a storage adapter and call a few
 * high-level operations instead of stitching primitives together.
 */
export function createAnalyticsSession(store: AnalyticsStore): AnalyticsSession {
  let cachedVisitorId: string | null = null;
  let queue: Promise<unknown> = Promise.resolve();

  // Serialize storage access so concurrent callers can't mint duplicate ids.
  function withLock<T>(fn: () => Promise<T>): Promise<T> {
    const run = queue.then(fn);
    queue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  const session: AnalyticsSession = {
    peekVisitorId() {
      return cachedVisitorId;
    },

    resolveVisitorId(handoffSearch) {
      return withLock(async () => {
        const fromHandoff = normalizeVisitorId(
          readParam(handoffSearch, BP_VISITOR_ID_PARAM),
        );
        if (fromHandoff) {
          cachedVisitorId = fromHandoff;
          await store.setVisitorId(fromHandoff);
          return fromHandoff;
        }

        const cached = normalizeVisitorId(cachedVisitorId);
        if (cached) {
          return cached;
        }

        const stored = normalizeVisitorId(await store.getVisitorId());
        if (stored) {
          cachedVisitorId = stored;
          return stored;
        }

        const created = createVisitorId();
        cachedVisitorId = created;
        await store.setVisitorId(created);
        return created;
      });
    },

    captureClickIds(handoffSearch) {
      return withLock(async () => {
        const incoming = parseClickIdsFromSearch(handoffSearch ?? "");
        const existing = await store.getClickIds();
        if (!incoming) {
          return existing;
        }

        const merged = mergeClickIds(existing, incoming);
        if (merged) {
          await store.setClickIds(merged);
        }
        return merged;
      });
    },

    async getFreshClickIds() {
      const stored = await store.getClickIds();
      if (!stored || !isClickIdsFresh(stored)) {
        return null;
      }
      return stored;
    },

    async resolveCampaignProps(handoffSearch) {
      await session.captureClickIds(handoffSearch);
      return stringProps({
        ...parseUtmParamsFromSearch(handoffSearch ?? ""),
        ...(await session.getFreshClickIds()),
      });
    },
  };
  return session;
}

function readParam(search: string | null | undefined, key: string) {
  if (!search) {
    return null;
  }
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  return params.get(key);
}

/** The non-empty string entries; drops the click ids' numeric `capturedAt`. */
function stringProps(values: Record<string, unknown>) {
  const props: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    if (typeof value === "string" && value) {
      props[key] = value;
    }
  }
  return props;
}
